/* pv-scroll-hint.js
 *
 * Progressive enhancement for horizontal scrollers. Three jobs.
 *
 * 1. EDGE FADE. iOS hides scrollbars at rest, so a swipe row just looks like
 *    the content got chopped off. We fade whichever edge still has content
 *    behind it.
 *
 *    Safari note: an earlier version put CSS custom properties INSIDE the mask
 *    gradient (`#000 var(--pv-fade-l)`). WebKit does not reliably re-evaluate a
 *    -webkit-mask-image when only a custom property changes, so the fade never
 *    moved on iPhone. The whole mask is now written inline with literal pixel
 *    values - no var() in the gradient - and -webkit-mask-size/repeat/position
 *    are pinned in CSS, because a mask on an overflow container otherwise gets
 *    sized to the SCROLLABLE area rather than the visible box in WebKit.
 *
 * 2. ARROWS (desktop). A fade tells you there is more; it does not let you get
 *    there. Plenty of people have a mouse with no horizontal scroll, so any row
 *    that actually overflows gets real prev/next buttons, in a nav row BELOW
 *    the scroller, left-aligned with the content. They disable themselves at
 *    each end and are real <button>s, so keyboard and screen-reader usable.
 *
 * 3. TAGGING. Adds classes the CSS can hook without :has(), which older iOS
 *    Safari drops (taking the whole rule with it):
 *      .pv-icon-row           - a SHORT row of icon boxes (<= 4)
 *      .pv-icon-row-scroller  - its scroll container
 *
 *    The <= 4 limit matters. The 4-up "benefit" rows (memberships icon band,
 *    home difference row) become a 2x2 grid on mobile. The Shop-by-Brand (7)
 *    and Shop-by-Category (14) rows must NOT - a 2-column grid of 14 items is
 *    seven rows tall. Those stay scrollers, and get the fade + arrows instead.
 */
(function () {
  'use strict';
  var FADE = 56;
  var MAX_ICON_ROW = 4;
  var tracked = [];

  function overflows(el) {
    return el.scrollWidth - el.clientWidth > 4;
  }

  function scrollableX(el) {
    var ox = getComputedStyle(el).overflowX;
    return ox === 'auto' || ox === 'scroll';
  }

  function setMask(el, left, right) {
    var g =
      'linear-gradient(to right, rgba(0,0,0,0) 0px, rgb(0,0,0) ' +
      left.toFixed(1) +
      'px, rgb(0,0,0) calc(100% - ' +
      right.toFixed(1) +
      'px), rgba(0,0,0,0) 100%)';
    el.style.webkitMaskImage = g;
    el.style.maskImage = g;
  }

  function svgArrow(dir) {
    var d = dir === 'left' ? 'M15 4 L7 12 L15 20' : 'M9 4 L17 12 L9 20';
    return (
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="' + d + '" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>'
    );
  }

  function addArrows(el) {
    var host = el.parentElement;
    if (!host || host.__pvArrows) return;
    host.__pvArrows = true;
    host.classList.add('pv-scroll-arrows-host');

    var nav = document.createElement('div');
    nav.className = 'pv-scroll-nav';

    function make(dir) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'pv-scroll-arrow pv-scroll-arrow--' + dir;
      b.setAttribute('aria-label', dir === 'left' ? 'Scroll left' : 'Scroll right');
      b.innerHTML = svgArrow(dir);
      b.addEventListener('click', function () {
        var step = Math.max(el.clientWidth * 0.8, 200);
        el.scrollBy({ left: dir === 'left' ? -step : step, behavior: 'smooth' });
      });
      nav.appendChild(b);
      return b;
    }
    el.__pvLeft = make('left');
    el.__pvRight = make('right');

    /* nav row sits directly after the scroller, so it lands underneath it and
       lines up with the left edge of the content */
    if (el.nextSibling) {
      host.insertBefore(nav, el.nextSibling);
    } else {
      host.appendChild(nav);
    }
  }

  function updateArrows(el) {
    if (!el.__pvLeft) return;
    var max = el.scrollWidth - el.clientWidth;
    var x = el.scrollLeft;
    el.__pvLeft.disabled = x <= 2;
    el.__pvRight.disabled = x >= max - 2;
  }

  function update(el) {
    if (!overflows(el)) {
      el.classList.remove('pv-scroll-hint');
      el.style.webkitMaskImage = '';
      el.style.maskImage = '';
      if (el.parentElement) el.parentElement.classList.remove('pv-has-arrows');
      return;
    }
    el.classList.add('pv-scroll-hint');
    var max = el.scrollWidth - el.clientWidth;
    var x = el.scrollLeft;
    setMask(el, Math.min(x, FADE), Math.min(Math.max(max - x, 0), FADE));
    addArrows(el);
    if (el.parentElement) el.parentElement.classList.add('pv-has-arrows');
    updateArrows(el);
  }

  function watch(el) {
    if (el.__pvHint) return;
    el.__pvHint = true;
    tracked.push(el);
    var ticking = false;
    el.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          update(el);
        });
      },
      { passive: true }
    );
    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        update(el);
      }).observe(el);
    }
    update(el);
  }

  function isIconCell(child) {
    if (child.classList.contains('icon-box')) return true;
    return (
      child.children.length === 1 &&
      child.firstElementChild.classList.contains('icon-box')
    );
  }

  function tagIconRows() {
    var main = document.getElementById('MainContent');
    if (!main) return;
    var rows = main.querySelectorAll('.icon-boxes__inner, .grid-block-content');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var kids = row.children;
      if (!kids.length || kids.length > MAX_ICON_ROW) continue;
      var iconCells = 0;
      for (var j = 0; j < kids.length; j++) {
        if (isIconCell(kids[j])) iconCells++;
      }
      if (iconCells !== kids.length) continue;
      row.classList.add('pv-icon-row');
      var sc = row.closest('.icon-boxes__wrapper, .grid-block');
      if (sc) sc.classList.add('pv-icon-row-scroller');
    }
  }

  function scan() {
    tagIconRows();
    var all = document.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.__pvHint) continue;
      if (el.classList.contains('pv-scroll-nav')) continue;
      if (scrollableX(el) && overflows(el)) watch(el);
    }
  }

  function refresh() {
    for (var i = 0; i < tracked.length; i++) update(tracked[i]);
    scan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  window.addEventListener('load', refresh);
  setTimeout(refresh, 400);
  setTimeout(refresh, 1200);
  setTimeout(refresh, 3000);

  var t;
  window.addEventListener(
    'resize',
    function () {
      clearTimeout(t);
      t = setTimeout(refresh, 150);
    },
    { passive: true }
  );

  document.addEventListener('shopify:section:load', refresh);
})();
